import React, { useState, useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Shield, CheckCircle2, Circle, AlertTriangle, Lock, Unlock, ChevronDown, ChevronRight, ExternalLink, ListTodo, AlertCircle } from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { FmeaStandard } from '../types';

// Specification-aware workflow configurations
const WORKFLOW_CONFIGS: Record<FmeaStandard, { steps: StepDef[], name: string }> = {
    'AIAG_VDA': {
        name: 'AIAG-VDA 7-Step',
        steps: [
            { step: 1, name: 'Planning & Preparation', shortName: 'Planning', requiredFields: [], projectFields: ['projectName'], description: 'Define scope, team, and timeline' },
            { step: 2, name: 'Structure Analysis', shortName: 'Structure', requiredFields: ['function', 'itemFunction'], projectFields: [], description: 'Identify system elements and functions' },
            { step: 3, name: 'Function Analysis', shortName: 'Function', requiredFields: ['requirement'], projectFields: [], description: 'Define functions and requirements' },
            { step: 4, name: 'Failure Analysis', shortName: 'Failure', requiredFields: ['failureMode', 'effects', 'causes'], projectFields: [], description: 'Identify failure modes, effects, and causes' },
            { step: 5, name: 'Risk Analysis', shortName: 'Risk', requiredFields: ['S', 'O', 'D'], projectFields: [], description: 'Rate severity, occurrence, and detection' },
            { step: 6, name: 'Optimization', shortName: 'Optimize', requiredFields: [], highRiskActions: true, projectFields: [], description: 'Add controls for high-risk items (AP=H)' },
            { step: 7, name: 'Results Documentation', shortName: 'Document', requiredFields: [], projectFields: [], description: 'Document results and obtain approvals' },
        ]
    },
    'AIAG_V4': {
        name: 'AIAG V4 5-Step',
        steps: [
            { step: 1, name: 'Scope Definition', shortName: 'Scope', requiredFields: [], projectFields: ['projectName'], description: 'Define FMEA scope' },
            { step: 2, name: 'Failure Mode Identification', shortName: 'Failures', requiredFields: ['failureMode', 'effects'], projectFields: [], description: 'Identify all failure modes' },
            { step: 3, name: 'Risk Assessment', shortName: 'Risk', requiredFields: ['S', 'O', 'D'], projectFields: [], description: 'Calculate RPN for each item' },
            { step: 4, name: 'Corrective Actions', shortName: 'Actions', requiredFields: [], highRiskActions: true, projectFields: [], description: 'Add actions for RPN > 100' },
            { step: 5, name: 'Review & Close', shortName: 'Close', requiredFields: [], projectFields: [], description: 'Review and document' },
        ]
    },
    'SAE_J1739': {
        name: 'SAE J1739',
        steps: [
            { step: 1, name: 'Scope Definition', shortName: 'Scope', requiredFields: [], projectFields: ['projectName'], description: 'Define FMEA scope' },
            { step: 2, name: 'Failure Mode Identification', shortName: 'Failures', requiredFields: ['failureMode', 'effects'], projectFields: [], description: 'Identify all failure modes' },
            { step: 3, name: 'Risk Assessment', shortName: 'Risk', requiredFields: ['S', 'O', 'D'], projectFields: [], description: 'Calculate RPN for each item' },
            { step: 4, name: 'Corrective Actions', shortName: 'Actions', requiredFields: [], highRiskActions: true, projectFields: [], description: 'Add actions for high-risk' },
            { step: 5, name: 'Review & Close', shortName: 'Close', requiredFields: [], projectFields: [], description: 'Review and document' },
        ]
    },
    'ISO_14971': {
        name: 'ISO 14971 5-Step',
        steps: [
            { step: 1, name: 'Hazard Identification', shortName: 'Hazards', requiredFields: ['failureMode'], projectFields: ['projectName'], description: 'Identify potential hazards' },
            { step: 2, name: 'Risk Estimation', shortName: 'Estimate', requiredFields: ['S', 'O'], projectFields: [], description: 'Estimate severity and probability' },
            { step: 3, name: 'Risk Evaluation', shortName: 'Evaluate', requiredFields: ['D'], projectFields: [], description: 'Evaluate risk acceptability' },
            { step: 4, name: 'Risk Control', shortName: 'Control', requiredFields: [], highRiskActions: true, projectFields: [], description: 'Implement risk control measures' },
            { step: 5, name: 'Residual Risk', shortName: 'Residual', requiredFields: [], projectFields: [], description: 'Evaluate residual risk' },
        ]
    },
    'ISO_26262': {
        name: 'ISO 26262 7-Step',
        steps: [
            { step: 1, name: 'Item Definition', shortName: 'Item', requiredFields: [], projectFields: ['projectName'], description: 'Define item under analysis' },
            { step: 2, name: 'Hazard Analysis', shortName: 'HARA', requiredFields: ['failureMode', 'effects'], projectFields: [], description: 'Hazard analysis and risk assessment' },
            { step: 3, name: 'ASIL Determination', shortName: 'ASIL', requiredFields: ['S', 'O', 'D'], projectFields: [], description: 'Determine ASIL level' },
            { step: 4, name: 'Safety Goals', shortName: 'Goals', requiredFields: [], projectFields: [], description: 'Define safety goals' },
            { step: 5, name: 'Safety Mechanisms', shortName: 'Mechanisms', requiredFields: [], highRiskActions: true, projectFields: [], description: 'Add safety mechanisms for ASIL C/D' },
            { step: 6, name: 'Verification', shortName: 'Verify', requiredFields: [], projectFields: [], description: 'Verify safety requirements' },
            { step: 7, name: 'Release', shortName: 'Release', requiredFields: [], projectFields: [], description: 'Release for production' },
        ]
    },
    'IEC_61508': {
        name: 'IEC 61508 SIL-Based',
        steps: [
            { step: 1, name: 'Hazard & Risk Analysis', shortName: 'HRA', requiredFields: ['failureMode', 'effects'], projectFields: ['projectName'], description: 'Identify hazards' },
            { step: 2, name: 'SIL Determination', shortName: 'SIL', requiredFields: ['S', 'O', 'D'], projectFields: [], description: 'Determine SIL level' },
            { step: 3, name: 'Safety Requirements', shortName: 'Req', requiredFields: [], projectFields: [], description: 'Define safety requirements' },
            { step: 4, name: 'Risk Reduction', shortName: 'Reduce', requiredFields: [], highRiskActions: true, projectFields: [], description: 'Implement risk reduction' },
            { step: 5, name: 'Validation', shortName: 'Validate', requiredFields: [], projectFields: [], description: 'Validate safety functions' },
        ]
    },
    'MIL_STD_1629A': {
        name: 'MIL-STD-1629A',
        steps: [
            { step: 1, name: 'System Definition', shortName: 'System', requiredFields: [], projectFields: ['projectName'], description: 'Define system boundaries' },
            { step: 2, name: 'Failure Mode Identification', shortName: 'FMECA', requiredFields: ['failureMode', 'effects', 'causes'], projectFields: [], description: 'Identify failure modes' },
            { step: 3, name: 'Criticality Analysis', shortName: 'CA', requiredFields: ['S', 'O'], projectFields: [], description: 'Determine criticality' },
            { step: 4, name: 'Compensating Provisions', shortName: 'Provisions', requiredFields: [], highRiskActions: true, projectFields: [], description: 'Add compensating provisions' },
            { step: 5, name: 'Documentation', shortName: 'Docs', requiredFields: [], projectFields: [], description: 'Document results' },
        ]
    },
    'CUSTOMER_CUSTOM': {
        name: 'Custom Standard',
        steps: [
            { step: 1, name: 'Setup', shortName: 'Setup', requiredFields: [], projectFields: ['projectName'], description: 'Project setup' },
            { step: 2, name: 'Analysis', shortName: 'Analyze', requiredFields: ['failureMode'], projectFields: [], description: 'Failure analysis' },
            { step: 3, name: 'Risk', shortName: 'Risk', requiredFields: ['S', 'O', 'D'], projectFields: [], description: 'Risk assessment' },
            { step: 4, name: 'Actions', shortName: 'Actions', requiredFields: [], highRiskActions: true, projectFields: [], description: 'Action items' },
            { step: 5, name: 'Close', shortName: 'Close', requiredFields: [], projectFields: [], description: 'Close out' },
        ]
    },
};


interface StepDef {
    step: number;
    name: string;
    shortName: string;
    requiredFields: string[];
    projectFields?: string[];
    highRiskActions?: boolean;
    description: string;
}

interface HighRiskItem {
    fmeaId: string;
    failureMode: string;
    function: string;
    S: number;
    O: number;
    D: number;
    RPN?: number;
    actionPriority?: string;
    hasAction: boolean;
    hasControls: boolean;
}

interface StepStatus {
    step: number;
    name: string;
    complete: number;
    total: number;
    percentage: number;
    missingItems: { fmeaId: string; failureMode: string; fields: string[] }[];
    tasksRequired: string[];
    isLocked: boolean;
    canApprove: boolean;
}

const WorkflowGatesPage: React.FC = () => {
    const { allData, activeProject, navigate, approveWorkflowStep } = useAppContext();
    const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([1]));

    // Get current project's FMEA standard from project metadata
    const projectStandard = useMemo((): FmeaStandard => {
        // Try to get from project metadata first
        const projectMeta = allData?.projectMetadata;
        if (projectMeta?.fmeaStandard) {
            return projectMeta.fmeaStandard as FmeaStandard;
        }
        // Default to AIAG_VDA
        return 'AIAG_VDA';
    }, [allData]);

    const workflowConfig = WORKFLOW_CONFIGS[projectStandard] || WORKFLOW_CONFIGS['AIAG_VDA'];

    // Get high-risk items needing actions
    const highRiskItems = useMemo((): HighRiskItem[] => {
        const projectFmeas = (allData?.fmeaItems || []).filter(
            f => !activeProject || activeProject === 'all' || f.projectName === activeProject
        );

        return projectFmeas
            .filter(f => {
                const ap = f.actionPriority;
                const rpn = f.RPN || ((f.S || 1) * (f.O || 1) * (f.D || 1));
                return ap === 'H' || rpn > 100;
            })
            .map(f => {
                // Check if recommended action exists (may be on fmea or as a separate field)
                const recAction = (f as any).recommendedAction || (f as any).action || '';
                return {
                    fmeaId: f.id,
                    failureMode: f.failureMode || 'Unknown',
                    function: f.function || f.itemFunction || '',
                    S: f.S || 0,
                    O: f.O || 0,
                    D: f.D || 0,
                    RPN: f.RPN || ((f.S || 1) * (f.O || 1) * (f.D || 1)),
                    actionPriority: f.actionPriority,
                    hasAction: !!(recAction && recAction.trim()),
                    hasControls: (f.preventionControls?.length || 0) > 0 || (f.detectionControls?.length || 0) > 0
                };
            });
    }, [allData, activeProject]);


    // Calculate step completion status
    const stepStatuses = useMemo((): StepStatus[] => {
        const projectFmeas = (allData?.fmeaItems || []).filter(
            f => !activeProject || activeProject === 'all' || f.projectName === activeProject
        );

        const total = projectFmeas.length || 0;

        return workflowConfig.steps.map((stepDef, index) => {
            const missingItems: StepStatus['missingItems'] = [];
            const tasksRequired: string[] = [];
            let complete = 0;

            if (total === 0) {
                tasksRequired.push('Add FMEA items to start workflow');
                return {
                    step: stepDef.step,
                    name: stepDef.name,
                    complete: 0,
                    total: 0,
                    percentage: 0,
                    missingItems: [],
                    tasksRequired,
                    isLocked: index > 0,
                    canApprove: false
                };
            }

            // Check for high-risk actions step
            if (stepDef.highRiskActions) {
                const needingAction = highRiskItems.filter(h => !h.hasAction || !h.hasControls);
                complete = highRiskItems.length - needingAction.length;

                needingAction.forEach(item => {
                    const missing: string[] = [];
                    if (!item.hasAction) missing.push('recommendedAction');
                    if (!item.hasControls) missing.push('controls');
                    missingItems.push({
                        fmeaId: item.fmeaId,
                        failureMode: item.failureMode,
                        fields: missing
                    });
                });

                if (needingAction.length > 0) {
                    tasksRequired.push(`Add actions for ${needingAction.length} high-risk items`);
                }

                return {
                    step: stepDef.step,
                    name: stepDef.name,
                    complete,
                    total: highRiskItems.length || 1,
                    percentage: highRiskItems.length > 0 ? Math.round((complete / highRiskItems.length) * 100) : 100,
                    missingItems: missingItems.slice(0, 10),
                    tasksRequired,
                    isLocked: false,
                    canApprove: needingAction.length === 0 && highRiskItems.length > 0
                };
            }

            // Regular field-based steps
            projectFmeas.forEach(fmea => {
                const missing: string[] = [];

                stepDef.requiredFields.forEach(field => {
                    const value = (fmea as any)[field];
                    const isEmpty = value === undefined || value === null || value === '' ||
                        (Array.isArray(value) && value.length === 0);

                    if (isEmpty) {
                        missing.push(field);
                    }
                });

                if (missing.length === 0) {
                    complete++;
                } else {
                    missingItems.push({
                        fmeaId: fmea.id,
                        failureMode: fmea.failureMode || 'Unknown',
                        fields: missing
                    });
                }
            });

            // Generate task descriptions
            if (stepDef.requiredFields.length > 0 && missingItems.length > 0) {
                stepDef.requiredFields.forEach(field => {
                    const count = missingItems.filter(m => m.fields.includes(field)).length;
                    if (count > 0) {
                        tasksRequired.push(`Add "${field}" to ${count} items`);
                    }
                });
            }

            const percentage = total > 0 ? Math.round((complete / total) * 100) : (stepDef.requiredFields.length === 0 ? 100 : 0);

            return {
                step: stepDef.step,
                name: stepDef.name,
                complete,
                total,
                percentage,
                missingItems: missingItems.slice(0, 10),
                tasksRequired,
                isLocked: false,
                canApprove: percentage === 100
            };
        });
    }, [allData, activeProject, workflowConfig, highRiskItems]);

    const toggleStep = (step: number) => {
        setExpandedSteps(prev => {
            const next = new Set(prev);
            if (next.has(step)) {
                next.delete(step);
            } else {
                next.add(step);
            }
            return next;
        });
    };

    const getStepIcon = (status: StepStatus) => {
        if (status.isLocked) return <Lock className="text-gray-400" size={20} />;
        if (status.percentage === 100) return <CheckCircle2 className="text-green-600" size={20} />;
        if (status.percentage > 0) return <Circle className="text-yellow-600" size={20} />;
        return <Circle className="text-gray-400" size={20} />;
    };

    const getProgressColor = (percentage: number) => {
        if (percentage === 100) return 'bg-green-500';
        if (percentage > 50) return 'bg-blue-500';
        if (percentage > 0) return 'bg-yellow-500';
        return 'bg-gray-300';
    };

    const overallProgress = stepStatuses.length > 0
        ? Math.round(stepStatuses.reduce((acc, s) => acc + s.percentage, 0) / stepStatuses.length)
        : 0;

    const totalTasksRequired = stepStatuses.reduce((acc, s) => acc + s.tasksRequired.length, 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                        <Shield className="text-primary" />
                        Workflow Gates
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-text-secondary">{workflowConfig.name} Compliance</p>
                        <ActiveProjectBadge showStandard={true} />
                    </div>
                </div>
                <ModuleInfoModal
                    title="Workflow Gates"
                    description="Track FMEA completion against your selected standard's workflow with specification-aware gate requirements."
                    benefits={[
                        "Specification-aware step requirements",
                        "Track step-by-step progress",
                        "Auto-detect high-risk items needing actions",
                        "Gate approvals before proceeding"
                    ]}
                    usageInstructions={[
                        "Review each step's completion status",
                        "Check 'Tasks Required' for what needs to be done",
                        "Fix gaps by clicking the FMEA link",
                        "Approve gates when 100% complete"
                    ]}
                />
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-text-secondary">Overall Progress</div>
                    <div className={`text-3xl font-bold mt-1 ${overallProgress === 100 ? 'text-green-600' :
                        overallProgress > 50 ? 'text-blue-600' : 'text-yellow-600'
                        }`}>
                        {overallProgress}%
                    </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-text-secondary">FMEA Items</div>
                    <div className="text-3xl font-bold mt-1 text-text-primary">
                        {stepStatuses[0]?.total || 0}
                    </div>
                </div>
                <div className="bg-card border border-red-200 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-red-700">High-Risk Items</div>
                    <div className="text-3xl font-bold mt-1 text-red-600">
                        {highRiskItems.length}
                    </div>
                    <div className="text-xs text-red-600 mt-1">
                        {highRiskItems.filter(h => !h.hasAction).length} need actions
                    </div>
                </div>
                <div className="bg-card border border-yellow-200 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-yellow-700">Tasks Required</div>
                    <div className="text-3xl font-bold mt-1 text-yellow-600">
                        {totalTasksRequired}
                    </div>
                </div>
            </div>

            {/* Task Summary Section */}
            {highRiskItems.length > 0 && (
                <div className="bg-card border border-red-200 rounded-lg shadow-sm overflow-hidden">
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200">
                        <h2 className="text-lg font-semibold flex items-center gap-2 text-red-800 dark:text-red-200">
                            <AlertCircle size={20} />
                            High-Risk Items Requiring Action
                        </h2>
                        <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                            These items have Action Priority = High or RPN &gt; 100 and need attention
                        </p>
                    </div>
                    <div className="divide-y divide-red-100 dark:divide-red-900">
                        {highRiskItems.slice(0, 10).map((item, i) => (
                            <div key={i} className="p-3 flex items-center justify-between hover:bg-red-50/50 dark:hover:bg-red-900/10">
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">{item.failureMode}</div>
                                    <div className="text-xs text-text-secondary">{item.function}</div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-center">
                                        <div className="text-xs text-text-secondary">S×O×D</div>
                                        <div className="font-semibold text-red-600">{item.RPN}</div>
                                    </div>
                                    <div className="flex gap-1">
                                        {!item.hasAction && (
                                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded">No Action</span>
                                        )}
                                        {!item.hasControls && (
                                            <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">No Controls</span>
                                        )}
                                        {item.hasAction && item.hasControls && (
                                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">✓ Complete</span>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => navigate('/fmea-editor', { highlight: { type: 'fmea', id: item.fmeaId } })}
                                        className="text-primary hover:underline flex items-center gap-1 text-sm"
                                    >
                                        <ExternalLink size={14} />
                                        Edit
                                    </button>
                                </div>
                            </div>
                        ))}
                        {highRiskItems.length > 10 && (
                            <div className="p-3 text-center text-sm text-text-secondary">
                                ... and {highRiskItems.length - 10} more high-risk items
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Step Cards */}
            <div className="space-y-4">
                {stepStatuses.map((status, index) => {
                    const stepDef = workflowConfig.steps[index];
                    const isExpanded = expandedSteps.has(status.step);

                    return (
                        <div
                            key={status.step}
                            className={`bg-card border rounded-lg shadow-sm overflow-hidden ${status.isLocked ? 'border-gray-200 opacity-60' :
                                status.percentage === 100 ? 'border-green-300' : 'border-border'
                                }`}
                        >
                            {/* Step Header */}
                            <div
                                className="p-4 flex items-center justify-between cursor-pointer hover:bg-background/50"
                                onClick={() => toggleStep(status.step)}
                            >
                                <div className="flex items-center gap-4">
                                    {getStepIcon(status)}
                                    <div>
                                        <div className="font-semibold flex items-center gap-2">
                                            Step {status.step}: {status.name}
                                            {status.percentage === 100 && (
                                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                                    Complete
                                                </span>
                                            )}
                                            {status.tasksRequired.length > 0 && (
                                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                                    {status.tasksRequired.length} tasks
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-sm text-text-secondary">
                                            {stepDef.description}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-right">
                                        <div className="font-semibold">{status.percentage}%</div>
                                        <div className="text-xs text-text-secondary">
                                            {status.complete}/{status.total} items
                                        </div>
                                    </div>
                                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full ${getProgressColor(status.percentage)}`}
                                            style={{ width: `${status.percentage}%` }}
                                        />
                                    </div>
                                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                </div>
                            </div>

                            {/* Expanded Content */}
                            {isExpanded && (
                                <div className="border-t border-border p-4 bg-background/30">
                                    {/* Tasks Required */}
                                    {status.tasksRequired.length > 0 && (
                                        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                                            <div className="text-sm font-medium mb-2 flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                                                <ListTodo size={16} />
                                                Tasks Required to Complete This Step
                                            </div>
                                            <ul className="space-y-1">
                                                {status.tasksRequired.map((task, i) => (
                                                    <li key={i} className="text-sm text-yellow-700 dark:text-yellow-300 flex items-center gap-2">
                                                        <Circle size={8} className="text-yellow-500" />
                                                        {task}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Missing Items */}
                                    {status.missingItems.length > 0 && (
                                        <div>
                                            <div className="text-sm font-medium mb-2 flex items-center gap-2">
                                                <AlertTriangle className="text-yellow-600" size={16} />
                                                Items Needing Attention ({status.total - status.complete})
                                            </div>
                                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                                {status.missingItems.map((item, i) => (
                                                    <div
                                                        key={i}
                                                        className="flex items-center justify-between bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded text-sm"
                                                    >
                                                        <div>
                                                            <span className="font-medium">{item.failureMode}</span>
                                                            <span className="text-text-secondary ml-2">
                                                                Missing: {item.fields.join(', ')}
                                                            </span>
                                                        </div>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate('/fmea-editor', { highlight: { type: 'fmea', id: item.fmeaId } });
                                                            }}
                                                            className="text-primary hover:underline flex items-center gap-1"
                                                        >
                                                            <ExternalLink size={14} />
                                                            Edit
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {status.percentage === 100 && status.tasksRequired.length === 0 && (
                                        <div className="flex items-center gap-2 text-green-600">
                                            <CheckCircle2 size={16} />
                                            <span className="text-sm">All requirements met for this step</span>
                                        </div>
                                    )}

                                    {/* Approval Button */}
                                    {status.canApprove && (
                                        <div className="mt-4 pt-4 border-t border-border">
                                            <button
                                                onClick={() => approveWorkflowStep(status.step.toString())}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                            >
                                                <Unlock size={16} />
                                                Approve Step {status.step}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Summary */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-text-secondary">
                    <strong>💡 Tip:</strong> This page shows {workflowConfig.name} workflow requirements.
                    Focus on completing tasks in order. High-risk items (AP=H or RPN&gt;100) need actions before Step 6 can pass.
                </p>
            </div>
        </div>
    );
};

export default WorkflowGatesPage;
